import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Gamepad2, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface GameAuthFormProps {
  onSignUp: (email: string, password: string, displayName: string) => Promise<boolean>;
  onSignIn: (email: string, password: string) => Promise<boolean>;
  onResetPassword: (email: string) => Promise<boolean>;
}

export function GameAuthForm({ onSignUp, onSignIn, onResetPassword }: GameAuthFormProps) {
  const [mode, setMode] = useState<"login" | "signup" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await onSignIn(email, password);
      } else if (mode === "signup") {
        if (password.length < 6) {
          toast.error("Password must be at least 6 characters");
          return;
        }
        await onSignUp(email, password, displayName || email.split("@")[0]);
      } else if (mode === "reset") {
        const ok = await onResetPassword(email);
        if (ok) setResetSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto border border-purple-500/30 bg-black/70 backdrop-blur-md shadow-game-neon">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-game-neon rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-game-neon-sm">
          <Gamepad2 className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl text-game-gradient">
          {mode === "login" ? "Enter the Game" : mode === "signup" ? "Create Account" : "Reset Password"}
        </CardTitle>
        <CardDescription className="text-gray-400">
          {mode === "login"
            ? "Sign in with your email and password"
            : mode === "signup"
            ? "Sign up and get $10 virtual balance to start"
            : resetSent
            ? "Check your email for a reset link"
            : "Enter your email to receive a password reset link"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <div className="relative">
              <User className="absolute left-3 top-3 w-4 h-4 text-purple-400" />
              <Input
                type="text"
                placeholder="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="pl-10 bg-black/60 border-purple-500/30 text-white placeholder:text-gray-600 focus-visible:ring-purple-500"
              />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-4 h-4 text-purple-400" />
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="pl-10 bg-black/60 border-purple-500/30 text-white placeholder:text-gray-600 focus-visible:ring-purple-500"
            />
          </div>
          {mode !== "reset" && (
          <div className="relative">
            <Lock className="absolute left-3 top-3 w-4 h-4 text-purple-400" />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password (min. 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="pl-10 pr-10 bg-black/60 border-purple-500/30 text-white placeholder:text-gray-600 focus-visible:ring-purple-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-gray-500 hover:text-gray-300 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          )}
          <Button type="submit" className="w-full font-bold btn-game-neon border-0" disabled={loading || (mode === "reset" && resetSent)}>
            {loading ? "Loading..." : mode === "login" ? "Sign In" : mode === "signup" ? "Sign Up" : resetSent ? "Email Sent ✓" : "Send Reset Link"}
          </Button>
        </form>
        <div className="text-center text-sm text-gray-500 mt-4 space-y-1">
          {mode === "login" && (
            <>
              <p className="text-[11px] text-gray-600 mb-2">
                Use a real email — you may need it for password recovery
              </p>
              <p>
                Don't have an account?{" "}
                <button onClick={() => setMode("signup")} className="text-game-pink hover:underline font-semibold">Sign Up</button>
              </p>
              <p>
                <button onClick={() => { setMode("reset"); setResetSent(false); }} className="text-game-cyan hover:underline font-semibold text-xs">Forgot password?</button>
              </p>
            </>
          )}
          {mode === "signup" && (
            <p>
              Already have an account?{" "}
              <button onClick={() => setMode("login")} className="text-game-pink hover:underline font-semibold">Sign In</button>
            </p>
          )}
          {mode === "reset" && (
            <p>
              <button onClick={() => { setMode("login"); setResetSent(false); }} className="text-game-pink hover:underline font-semibold">Back to Sign In</button>
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
